import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/shared/ErrorFallback";
import { formatReference, isChunkLoadError } from "@/lib/error-kind";

/**
 * Filet d'erreur du routeur — **le seul** qui couvre les erreurs de route.
 *
 * `createBrowserRouter` (React Router v7) intercepte lui-même les erreurs de rendu d'un composant
 * de route et les échecs de `lazy()`, puis rend l'`errorElement` le plus proche. Il ne les laisse
 * donc **jamais** remonter jusqu'au `<Sentry.ErrorBoundary>` de `main.tsx`, qui est monté *au-dessus*
 * de `<RouterProvider>`. Sans `errorElement`, React Router retombait sur son composant par défaut :
 * deux titres HTML non stylés sur fond vide — ce que les familles décrivent comme un « écran
 * entièrement blanc » (bilans des 30/07, 05/08 et 06/08) — et, plus grave, **aucun signalement à
 * Sentry**, ce qui explique qu'aucune de ces occurrences n'ait jamais laissé de trace exploitable
 * malgré des horodatages précis.
 *
 * Couvre aussi le second échec de chargement de chunk dans un même onglet : le garde-fou
 * `vite:preloadError` de `main.tsx` ne recharge qu'une fois par session (drapeau jamais remis à
 * zéro), donc l'échec suivant arrivait ici sans aucun recours.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    // Une réponse 404/403 de route n'est pas un défaut applicatif : inutile de polluer Sentry.
    if (isRouteErrorResponse(error)) return;
    if (isChunkLoadError(error)) {
      Sentry.captureException(error, {
        tags: { source: "react-router-error-element", error_kind: "chunk-load-failure" },
        fingerprint: ["chunk-load-failure"],
        level: "warning",
      });
      return;
    }
    Sentry.captureException(error, { tags: { source: "react-router-error-element" } });
  }, [error]);

  const eventId = Sentry.lastEventId();

  if (isRouteErrorResponse(error)) {
    return (
      <ErrorFallback
        fullScreen
        title="Cette page n'est pas accessible"
        description="Le contenu demandé est introuvable ou ne t'est pas accessible."
      />
    );
  }

  // Le bouton « Réessayer » d'ErrorFallback recharge la page par défaut, ce qui est exactement
  // ce qu'il faut pour le cas le plus fréquent ici — un chunk manquant après un déploiement :
  // recharger récupère un index.html à jour, donc les nouvelles URL de chunks.
  return (
    <ErrorFallback
      fullScreen
      error={error instanceof Error ? error : null}
      reference={formatReference(eventId)}
    />
  );
}
